import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

// Types mirrored from ocr-client — not a declared dep of the web app

export interface LlmProviderInfo {
  name: string;
  displayName: string;
  models: { id: string; name: string; tier: string }[];
}

export interface LlmTenantConfig {
  providerName: string;
  enabled: boolean;
  maskedApiKey: string;
  modelId: string | null;
  dailyBudgetUsd: number;
  monthlyBudgetUsd: number;
  defaultRoutingMode: string;
  fallbackConfidenceThreshold: number;
  createdAt: string;
  updatedAt: string;
}

export interface LlmConfigUpdate {
  apiKey?: string;
  modelId?: string;
  enabled?: boolean;
  dailyBudgetUsd?: number;
  monthlyBudgetUsd?: number;
  defaultRoutingMode?: string;
  fallbackConfidenceThreshold?: number;
}

export interface LlmUsageSummary {
  dailySpendUsd: number;
  monthlySpendUsd: number;
  totalCalls: number;
  successfulCalls: number;
  byProvider: Record<string, {
    calls: number;
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
  }>;
}

export interface LlmUsageRecord {
  id: number;
  providerName: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  latencyMs: number;
  fieldType: string | null;
  fieldName: string | null;
  success: boolean;
  errorMessage: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════
// useLlmConfig — fetch LLM providers and tenant configuration
// ═══════════════════════════════════════════════════════════════

export function useLlmConfig() {
  const [providers, setProviders] = useState<LlmProviderInfo[]>([]);
  const [configs, setConfigs] = useState<LlmTenantConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    try {
      const data = await api.get<LlmProviderInfo[]>("/llm/providers");
      setProviders(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load providers");
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const data = await api.get<LlmTenantConfig[]>("/llm/config");
      setConfigs(data);
      setError(null);
    } catch (e: unknown) {
      // 503 means encryption not configured — surface as warning, not fatal
      if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 503) {
        setError("encryption_not_configured");
      } else {
        setError(e instanceof Error ? e.message : "Failed to load config");
      }
    }
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([loadProviders(), loadConfig()]);
    setIsLoading(false);
  }, [loadProviders, loadConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const updateConfig = useCallback(
    async (provider: string, update: LlmConfigUpdate) => {
      const result = await api.put<LlmTenantConfig>(
        `/llm/config/${encodeURIComponent(provider)}`,
        update,
      );
      setConfigs((prev) => {
        const idx = prev.findIndex((c) => c.providerName === provider);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = result;
          return next;
        }
        return [...prev, result];
      });
      return result;
    },
    [],
  );

  const deleteConfig = useCallback(async (provider: string) => {
    await api.delete(`/llm/config/${encodeURIComponent(provider)}`);
    setConfigs((prev) => prev.filter((c) => c.providerName !== provider));
  }, []);

  const validateKey = useCallback(
    async (provider: string, apiKey: string): Promise<boolean> => {
      const result = await api.post<{ valid: boolean }>(
        `/llm/config/${encodeURIComponent(provider)}/validate`,
        { apiKey },
      );
      return result.valid;
    },
    [],
  );

  const getConfigFor = useCallback(
    (providerName: string): LlmTenantConfig | undefined =>
      configs.find((c) => c.providerName === providerName),
    [configs],
  );

  return {
    providers,
    configs,
    isLoading,
    error,
    refresh: load,
    updateConfig,
    deleteConfig,
    validateKey,
    getConfigFor,
  };
}
